import DOMPurify from 'dompurify';

/**
 * The rich-text contract for customer-document fields, in one place.
 *
 * These fields (`estimates.scope_of_work`, `terms_text`, `invoices.public_notes` / `terms_text`)
 * store HTML and are rendered onto the customer's PDF by the api worker, which runs the stored
 * value through `sanitizeRichHtml` (qelm-backend `src/services/rich-html.ts`) first.
 *
 * ⚠ THIS ALLOWLIST MUST MATCH THAT ONE. It is copied from `SAFE_TAGS` there, and the empty
 * attribute list is not an oversight: the backend's `safeTag` regex matches a bare `<tag>` only, so
 * ANY tag carrying an attribute is escaped into visible text on the PDF. That has two consequences
 * worth stating plainly, because both look like bugs when you meet them from the other end:
 *
 *   • There are NO LINKS. `<a href="…">` cannot survive the backend, so the editor does not offer
 *     one. Shipping a link button would let somebody format a quote that reads correctly in the
 *     dashboard and arrives at the customer with a raw `<a href=…>` printed in the middle of it.
 *   • There is no code, no code block, no horizontal rule, and no heading below h3 — TipTap's
 *     StarterKit ships all of those and they are switched off in `RichTextEditor` for this reason.
 *
 * If the backend list changes, change this one in the same commit. A drift in either direction is
 * invisible until a customer's document is already wrong.
 */
export const RICH_TEXT_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ol',
  'ul',
  'li',
  'h1',
  'h2',
  'h3',
  'blockquote',
] as const;

/**
 * Stored document HTML → HTML that is safe to hand to `dangerouslySetInnerHTML`.
 *
 * The tenant's own staff type into these fields, but "our own users" is not a trust boundary: the
 * value is stored, shared across a workspace, and rendered in another person's session, so an
 * injected `<script>` or an `onerror` attribute would run for whoever opens the record next. Every
 * render goes through here.
 *
 * KEEP_CONTENT IS LEFT ON (the default), so a disallowed element is UNWRAPPED and the tenant's
 * words survive: `<a href=…>terms</a>` becomes `terms`, and an `<h4>` becomes its own text rather
 * than vanishing. The alternative was tried and rejected — `KEEP_CONTENT: false` silently deleted
 * the text inside any tag outside the allowlist, which edits a customer's document without telling
 * anybody. `richText.ts` argues the same way about entities, and it is right: losing a tenant's
 * text is worse than showing it unformatted.
 *
 * That is safe because it is NOT the mechanism protecting against script injection. DOMPurify's
 * `FORBID_CONTENTS` set still discards the bodies of `script`, `style`, `noscript`, `template` and
 * friends, so nothing unwraps executable source into the page as visible text.
 */
export function sanitizeRichTextHtml(value: string | null | undefined): string {
  const input = String(value ?? '').trim();
  if (!input) return '';

  return DOMPurify.sanitize(input, {
    // '#text' is NOT decoration. DOMPurify treats text nodes as a tag named `#text`, so a custom
    // ALLOWED_TAGS list that omits it strips every character of visible content and returns bare
    // tags — `<p>Text</p>` comes back as `<p></p>`. It is kept out of RICH_TEXT_TAGS because that
    // list documents the backend's element allowlist, and `#text` is not an element.
    ALLOWED_TAGS: [...RICH_TEXT_TAGS, '#text'],
    ALLOWED_ATTR: [],
  });
}

/** True when the stored value carries nothing a reader would see — used to pick the em-dash. */
export function isRichTextEmpty(value: string | null | undefined): boolean {
  const clean = sanitizeRichTextHtml(value);
  if (!clean) return true;
  // A field cleared in the editor still stores "<p></p>"; TipTap has no concept of "no document".
  //
  // The non-breaking space is matched by ESCAPE, never as a literal. A literal U+00A0 in the source
  // looks exactly like a normal space and normalises to one on the way through an editor, which
  // silently turns the replace into a no-op — it did exactly that here once.
  return (
    clean
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/[\s\u00a0]+/g, ' ')
      .trim() === ''
  );
}
